'use client';

import { GALA_ABI } from '@lfg/abis';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { Address } from 'viem';
import { useBalance } from 'wagmi';
import { readContracts } from 'wagmi/actions';

import {
  GALA_SC_ADDRESS,
  NODE_SALE_V1_SC_ADDRESS,
  USDC_ABI,
  USDC_SC_ADDRESS,
  USDT_ABI,
  USDT_SC_ADDRESS,
} from '@/global/configs/common.config';
import { Coin } from '@/global/enums/common.enums';
import { useWeb3 } from '@/hooks';
import { useAppSelector } from '@/store/utils';

interface TokenBalance {
  [key: string]: any;
}

interface BalanceState {
  lixAllowance: any;
  lixBalance: any;
  usdtBalance: any;
  usdtAllowance: any;
  usdcBalance: any;
  usdcAllowance: any;
}

interface ContractParams {
  abi: any[];
  address: Address;
  functionName: string;
  args: any[];
}

interface Web3BalanceContextType {
  balances: TokenBalance;
  allowance: TokenBalance;
  lixAllowance: any;
  lixBalance: any;
  usdcAllowance: any;
  usdcBalance: any;
  usdtAllowance: any;
  usdtBalance: any;
  getWalletBalance: () => Promise<void>;
  isLoading: boolean;
  error: Error | null;
}

const DEFAULT_BALANCE_STATE: BalanceState = {
  lixAllowance: BigInt(0),
  lixBalance: BigInt(0),
  usdtBalance: BigInt(0),
  usdtAllowance: BigInt(0),
  usdcBalance: BigInt(0),
  usdcAllowance: BigInt(0),
};

const Web3BalanceContext = createContext<Web3BalanceContextType | null>(null);

const createContractConfig = ({ abi, address, functionName, args }: ContractParams): ContractParams => ({
  abi,
  address,
  functionName,
  args,
});

export const useWeb3Balance = () => {
  const context = useContext(Web3BalanceContext);
  if (!context) {
    throw new Error('useWeb3Balance must be used within a Web3BalanceProvider');
  }
  return context;
};

interface Web3BalanceProviderProps {
  children: React.ReactNode;
}

export const Web3BalanceProvider: React.FC<Web3BalanceProviderProps> = ({ children }) => {
  const { address, config, isWrongNetwork } = useWeb3();
  const { data: ethBalance, refetch: refetchEthBalance } = useBalance({ address });
  const [balanceState, setBalanceState] = useState<BalanceState>(DEFAULT_BALANCE_STATE);
  console.log('🚀 ~ balanceState:', balanceState);
  const { projectInfo } = useAppSelector(state => state.project);
  const { nodeVersion } = useAppSelector(state => state.app);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const allowanceSpender = nodeVersion === 'v2' ? projectInfo?.sale : NODE_SALE_V1_SC_ADDRESS;

  const getWalletBalance = useCallback(async () => {
    if (!address || isWrongNetwork || !allowanceSpender) {
      setBalanceState(DEFAULT_BALANCE_STATE);
      return;
    }

    setIsLoading(true);
    setError(null);

    const contractConfigs = [
      // TODO - Later add LIX contract
      createContractConfig({
        abi: GALA_ABI,
        address: GALA_SC_ADDRESS,
        functionName: 'allowance',
        args: [address, allowanceSpender],
      }),
      createContractConfig({
        abi: GALA_ABI,
        address: GALA_SC_ADDRESS,
        functionName: 'balanceOf',
        args: [address],
      }),
      createContractConfig({
        abi: USDT_ABI,
        address: USDT_SC_ADDRESS,
        functionName: 'allowance',
        args: [address, allowanceSpender],
      }),
      createContractConfig({
        abi: USDT_ABI,
        address: USDT_SC_ADDRESS,
        functionName: 'balanceOf',
        args: [address],
      }),
      createContractConfig({
        abi: USDC_ABI,
        address: USDC_SC_ADDRESS,
        functionName: 'allowance',
        args: [address, allowanceSpender],
      }),
      createContractConfig({
        abi: USDC_ABI,
        address: USDC_SC_ADDRESS,
        functionName: 'balanceOf',
        args: [address],
      }),
    ];

    try {
      const [data] = await Promise.all([readContracts(config, { contracts: contractConfigs }), refetchEthBalance()]);
      console.log('🚀 ~ getWalletBalance ~ data:', data);

      if (!data?.length) {
        setBalanceState(DEFAULT_BALANCE_STATE);
        return;
      }

      const lixAllowanceRes = data?.[0]?.result ?? BigInt(0);
      const lixBalanceRes = data?.[1]?.result ?? BigInt(0);
      const usdtAllowanceRes = data?.[2]?.result ?? BigInt(0);
      const usdtBalanceRes = data?.[3]?.result ?? BigInt(0);
      const usdcAllowanceRes = data?.[4]?.result ?? BigInt(0);
      const usdcBalanceRes = data?.[5]?.result ?? BigInt(0);
      setBalanceState({
        lixAllowance: lixAllowanceRes,
        lixBalance: lixBalanceRes,
        usdtBalance: usdtBalanceRes,
        usdtAllowance: usdtAllowanceRes,
        usdcBalance: usdcBalanceRes,
        usdcAllowance: usdcAllowanceRes,
      });
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch wallet balance'));
      setBalanceState(DEFAULT_BALANCE_STATE);
    } finally {
      setIsLoading(false);
    }
  }, [address, config, isWrongNetwork, refetchEthBalance, allowanceSpender]);

  // Set up automatic refresh interval
  useEffect(() => {
    if (!address || isWrongNetwork) return;

    getWalletBalance();
  }, [address, isWrongNetwork, getWalletBalance]);

  const ethValue = ethBalance?.value || BigInt(0);

  const formattedBalances = {
    balances: {
      [Coin.LIX]: balanceState.lixBalance,
      [Coin.USDT]: balanceState.usdtBalance,
      [Coin.USDC]: balanceState.usdcBalance,
      [Coin.BSC]: ethValue,
    },
    allowance: {
      [Coin.LIX]: balanceState.lixAllowance,
      [Coin.USDT]: balanceState.usdtAllowance,
      [Coin.USDC]: balanceState.usdcAllowance,
      [Coin.BSC]: BigInt(0),
    },
  };

  const contextValue = {
    ...formattedBalances,
    ...balanceState,
    getWalletBalance,
    isLoading,
    error,
  };

  return <Web3BalanceContext.Provider value={contextValue}>{children}</Web3BalanceContext.Provider>;
};
